/** Reading a configured space back as a bundle manifest, which is what the studio then publishes. */

import type {
  ActivityManifest,
  BundleMember,
  CanvasArrangement,
  EventBinding,
  LayerPlacement,
  ProposedBinding,
} from "@nebulize/sdk";

import type { AddedActivityRow } from "../../types";

/** A member name is an identifier the bundle owns, so a customer's own label is slugged. */
export function memberName(row: AddedActivityRow, taken: Set<string>): string {
  const base =
    row.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/gu, "_")
      .replace(/^_+|_+$/gu, "") || row.activity_id;
  let name = base;
  let at = 2;
  while (taken.has(name)) {
    name = `${base}_${String(at)}`;
    at += 1;
  }
  taken.add(name);
  return name;
}

/** Where this row sits on each canvas kind, read off the space's own arrangement. */
function arrangementOf(
  row: AddedActivityRow,
  arrangement: CanvasArrangement | undefined,
): Record<string, LayerPlacement> {
  const held: Record<string, LayerPlacement> = {};
  for (const [kind, byName] of Object.entries(arrangement?.canvases ?? {})) {
    const found = byName[row.name];
    if (found !== undefined) held[kind] = found;
  }
  return held;
}

export interface Captured {
  readonly manifest: ActivityManifest;
  /** How many wires were left out because an end is no longer added. */
  readonly dropped: number;
}

/**
 * The space's added activities, their settings, their placements and their wires, as a bundle.
 * A version range is `^` on the pinned version, which is what a bundle claims to work with.
 */
export function captureAsBundle({
  spaceName,
  rows,
  arrangement,
  bindings,
}: {
  readonly spaceName: string;
  readonly rows: readonly AddedActivityRow[];
  readonly arrangement: CanvasArrangement | undefined;
  readonly bindings: readonly EventBinding[];
}): Captured {
  const taken = new Set<string>();
  const named = new Map<string, string>();
  const members: BundleMember[] = rows.map((row) => {
    const name = memberName(row, taken);
    named.set(row.added_activity_id, name);
    return {
      activity_id: row.activity_id,
      version: `^${row.activity_version}`,
      name,
      settings: row.settings,
      arrangement: arrangementOf(row, arrangement),
    };
  });

  const proposed: ProposedBinding[] = [];
  let dropped = 0;
  for (const one of bindings) {
    const source = named.get(one.source_added_activity_id);
    const target = named.get(one.target_added_activity_id);
    if (source === undefined || target === undefined) {
      dropped += 1;
      continue;
    }
    proposed.push({
      source,
      export: one.export,
      target,
      trigger: one.trigger,
      enabled_by_default: one.enabled !== false,
    });
  }

  const id =
    spaceName
      .toLowerCase()
      .replace(/[^a-z0-9]+/gu, "_")
      .replace(/^_+|_+$/gu, "") || "my_bundle";

  return {
    dropped,
    manifest: {
      schema_version: 1,
      activity: {
        id,
        name: spaceName,
        version: "0.1.0",
        sdk: "^0.1.0",
        description: "",
      },
      entities: {},
      effects: {},
      abilities: {},
      canvases: {},
      submissions: {},
      settings: [],
      requires: {},
      provides: {},
      visibility: {},
      events: { publishes: [], exports: {}, triggers: {} },
      bundle: { members, proposed_bindings: proposed, removes_members: false },
    },
  };
}
